/** Artifacts — list view (server-side pagination/sort/search + delete). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { MoreHorizontal, Pencil, Plus, Trash2 } from "@/components/ui/icon";
import type { Artifact } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import { StatusBadge } from "@/components/app/StatusBadge";

import {
  DataTable,
  PageHeader,
  SearchBox,
  useSearchFilter,
  type RefineTableCore,
} from "../projects/_shared";
import { humanize } from "../projects/_form";

export function ArtifactList() {
  const go = useGo();
  const { editUrl, showUrl, createUrl } = useNavigation();
  const { mutate: deleteArtifact } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<Artifact | null>(
    null,
  );

  const columns = useMemo<ColumnDef<Artifact, unknown>[]>(
    () => [
      { id: "title", accessorKey: "title", header: "Title" },
      {
        id: "artifact_type",
        accessorKey: "artifact_type",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="outline">{humanize(row.original.artifact_type)}</Badge>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "priority",
        accessorKey: "priority",
        header: "Priority",
        cell: ({ row }) =>
          row.original.priority ? (
            <Badge variant="secondary">{humanize(row.original.priority)}</Badge>
          ) : (
            "—"
          ),
      },
      {
        id: "due_date",
        accessorKey: "due_date",
        header: "Due",
        cell: ({ row }) =>
          row.original.due_date
            ? new Date(row.original.due_date).toLocaleDateString()
            : "—",
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onEdit={() => go({ to: editUrl("artifacts", row.original.id) })}
            onShow={() => go({ to: showUrl("artifacts", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  const table = useTable<Artifact, HttpError>({
    columns,
    refineCoreProps: {
      resource: "artifacts",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = useSearchFilter(core.setFilters);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Artifacts"
        description="Every tracked unit of work in the operating core."
        action={
          <Button onClick={() => go({ to: createUrl("artifacts") })}>
            <Plus className="mr-2 h-4 w-4" /> New Artifact
          </Button>
        }
      />

      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder="Search artifacts…"
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No artifacts yet."
        onRowClick={(a) => go({ to: showUrl("artifacts", a.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete artifact?"
        description={
          <>
            This permanently deletes{" "}
            <span className="font-medium">{pendingDelete?.title}</span>. This
            action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deleteArtifact({ resource: "artifacts", id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

function RowActions({
  onEdit,
  onShow,
  onDelete,
}: {
  onEdit: () => void;
  onShow: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>View</DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit}>
          <Pencil className="mr-2 h-4 w-4" /> Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ArtifactList;
